import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { rpApi } from '@/lib/api-client';
import { useAuthStore } from '@/stores/auth-store';

export type RecurringFrequency =
  | 'daily'
  | 'weekly'
  | 'bi_weekly'
  | 'monthly'
  | 'quarterly'
  | 'half_yearly'
  | 'yearly'
  | 'once';

export const RECURRING_FREQUENCIES: RecurringFrequency[] = [
  'daily',
  'weekly',
  'bi_weekly',
  'monthly',
  'quarterly',
  'half_yearly',
  'yearly',
];

export const RECURRING_FREQUENCY_LABELS: Record<RecurringFrequency, string> = {
  daily: 'Daily',
  weekly: 'Weekly',
  bi_weekly: 'Bi-Weekly',
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  half_yearly: 'Half-Yearly',
  yearly: 'Yearly',
  once: 'One-time',
};
export type RecurringStatus = 'active' | 'paused' | 'cancelled' | 'scheduled' | 'completed';

export interface RecurringSale {
  id: string;
  tenantId: string;
  customerId: string | null;
  merchantId: string | null;
  locationId: string | null;
  sourceSaleId: string | null;

  transactionType: string;
  amount: string;
  currency: string;
  memo: string | null;
  businessName: string | null;
  paymentMethod: 'ach' | 'card';

  // ACH snapshot
  accountType: string | null;
  accountCategory: string | null;
  routingNumber: string | null;
  accountNumber: string | null;
  authorizationType: string | null;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  phone: string | null;
  streetAddress: string | null;
  city: string | null;
  state: string | null;
  zipCode: string | null;
  idType: string | null;
  dlState: string | null;
  dlNumber: string | null;

  // Card snapshot
  cardLastFour: string | null;
  cardHolderName: string | null;
  cardExpirationDate: string | null;
  cardZipCode: string | null;
  cardStreetAddress: string | null;

  receiptDelivery: string | null;

  frequency: RecurringFrequency | 'once';
  startAt: string;
  endAt: string | null;
  nextRunAt: string;
  lastRunAt: string | null;
  status: RecurringStatus;

  createdAt: string;
  updatedAt: string;
}

interface ListParams {
  status?: RecurringStatus;
  customerId?: string;
  locationId?: string;
  page?: number;
  perPage?: number;
}

// `enabled: false` skips the request for a viewer without Recurring & Scheduled.
export function useRecurringSales(params?: ListParams, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['recurring-sales', params],
    queryFn: () =>
      rpApi.get<{
        data: RecurringSale[];
        pagination: { page: number; perPage: number; total: number; totalPages: number };
      }>('/recurring-sales', params as Record<string, string | number | undefined>),
    enabled: options?.enabled ?? true,
  });
}

export function useCreateRecurringSale() {
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);
  return useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      rpApi.post<{ data: RecurringSale }>('/recurring-sales', { ...data, initiatedByEmail: user?.email ?? undefined }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['recurring-sales'] });
    },
  });
}

export function useUpdateRecurringSale() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      rpApi.put<{ data: RecurringSale }>(`/recurring-sales/${id}`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['recurring-sales'] });
    },
  });
}

export function useCancelRecurringSale() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => rpApi.delete<{ data: RecurringSale }>(`/recurring-sales/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['recurring-sales'] });
    },
  });
}
